import React, { useState } from 'react';
import { Globe, Key, Shield, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import Input from '../ui/Input';
import Button from '../ui/Button';

interface WooCommerceFormData {
  baseUrl: string;
  consumerKey: string;
  consumerSecret: string;
}

interface WooCommerceSetupProps {
  onSave: (data: WooCommerceFormData) => Promise<void>;
  onTestConnection: () => Promise<{ ok: boolean; productCount?: number; error?: string }>;
  isSaving: boolean;
  isTesting: boolean;
}

const WooCommerceSetup: React.FC<WooCommerceSetupProps> = ({
  onSave,
  onTestConnection,
  isSaving,
  isTesting,
}) => {
  const [formData, setFormData] = useState<WooCommerceFormData>({
    baseUrl: '',
    consumerKey: '',
    consumerSecret: '',
  });
  const [showSecret, setShowSecret] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; productCount?: number; error?: string } | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof WooCommerceFormData, string>>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof WooCommerceFormData, string>> = {};

    if (!formData.baseUrl.trim()) {
      newErrors.baseUrl = 'Store URL is required';
    } else {
      try {
        new URL(formData.baseUrl);
      } catch {
        newErrors.baseUrl = 'Please enter a valid URL (e.g., https://your-store.com)';
      }
    }

    if (!formData.consumerKey.trim()) {
      newErrors.consumerKey = 'Consumer Key is required';
    } else if (!formData.consumerKey.startsWith('ck_')) {
      newErrors.consumerKey = 'Consumer Key should start with "ck_"';
    }

    if (!formData.consumerSecret.trim()) {
      newErrors.consumerSecret = 'Consumer Secret is required';
    } else if (!formData.consumerSecret.startsWith('cs_')) {
      newErrors.consumerSecret = 'Consumer Secret should start with "cs_"';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof WooCommerceFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Clear test result when form changes
    setTestResult(null);
  };

  const handleTestConnection = async () => {
    if (!validateForm()) return;
    
    // First save the config, then test
    try {
      await onSave(formData);
      const result = await onTestConnection();
      setTestResult(result);
    } catch (error) {
      setTestResult({ ok: false, error: 'Failed to test connection' });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    await onSave(formData);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b dark:border-gray-700 border-gray-200">
        <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
          <Shield className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h3 className="font-semibold dark:text-white text-gray-900">WooCommerce Configuration</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Enter your WooCommerce REST API credentials
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* Store URL */}
        <div>
          <Input
            label="Store URL"
            id="wc-baseUrl"
            name="wc-baseUrl"
            type="url"
            autoComplete="off"
            placeholder="https://your-store.com"
            value={formData.baseUrl}
            onChange={handleInputChange('baseUrl')}
            icon={<Globe className="w-4 h-4 text-gray-400" />}
          />
          {errors.baseUrl && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.baseUrl}</p>
          )}
        </div>

        {/* Consumer Key */}
        <div>
          <Input
            label="Consumer Key"
            id="wc-consumerKey"
            name="wc-consumerKey"
            type="text"
            autoComplete="off"
            placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            value={formData.consumerKey}
            onChange={handleInputChange('consumerKey')}
            icon={<Key className="w-4 h-4 text-gray-400" />}
          />
          {errors.consumerKey && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.consumerKey}</p>
          )}
        </div>

        {/* Consumer Secret */}
        <div>
          <div className="relative">
            <Input
              label="Consumer Secret"
              id="wc-consumerSecret"
              name="wc-consumerSecret"
              type={showSecret ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={formData.consumerSecret}
              onChange={handleInputChange('consumerSecret')}
              icon={<Key className="w-4 h-4 text-gray-400" />}
            />
            <button
              type="button"
              onClick={() => setShowSecret(!showSecret)}
              className="absolute right-3 top-[34px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.consumerSecret && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.consumerSecret}</p>
          )}
        </div>
      </div>

      {/* Test Result */}
      {testResult && (
        <div
          className={`p-4 rounded-lg border ${
            testResult.ok
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}
        >
          <div className="flex items-start gap-3">
            {testResult.ok ? (
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            )}
            <div>
              <p className={`font-medium ${testResult.ok ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
                {testResult.ok ? 'Connection Successful!' : 'Connection Failed'}
              </p>
              {testResult.ok && testResult.productCount !== undefined && (
                <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                  Found {testResult.productCount.toLocaleString()} products in your store
                </p>
              )}
              {!testResult.ok && testResult.error && (
                <p className="text-sm text-red-600 dark:text-red-500 mt-1">{testResult.error}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help text */}
      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium dark:text-white text-gray-900 mb-2">
          How to get your API credentials:
        </h4>
        <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-decimal list-inside">
          <li>Go to your WordPress admin panel</li>
          <li>Navigate to WooCommerce → Settings → Advanced → REST API</li>
          <li>Click "Add key" and generate new API credentials</li>
          <li>Set permissions to "Read" for catalog sync</li>
          <li>Copy the Consumer Key and Consumer Secret</li>
        </ol>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t dark:border-gray-700 border-gray-200">
        <Button
          variant="secondary"
          onClick={handleTestConnection}
          disabled={isTesting || isSaving}
          isLoading={isTesting}
        >
          Test Connection
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || isTesting}
          isLoading={isSaving}
        >
          Save Integration
        </Button>
      </div>
    </div>
  );
};

export default WooCommerceSetup;
